import CustomAvatar from "@/components/CustomAvatar";
import Rating from "@/components/Rating";
import { Button } from "@/components/ui/button";
import { PollWithOptions } from "@/lib/server-functions/helper-functions/fetch-polls";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { motion } from "framer-motion";
import {
  Bookmark,
  ChartNoAxesColumn,
  Dot,
  EllipsisVertical,
  MessageSquare,
  Trash,
  Users,
} from "lucide-react";
import { forwardRef, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DeleteDialog from "@/components/Polls/DeleteDialog";
import { authClient } from "@/lib/better-auth/client";

const roundToOneDecimal = (num: number) => num.toFixed(1);

interface PollCardProps {
  poll: PollWithOptions;
  onVote: (optionId: string) => void;
  onReaction: (reaction: "upvote" | "downvote") => void;
  onBookmark: () => void;
  onDelete: () => void;
  disabled?: boolean;
}

const PollCard = forwardRef<HTMLDivElement, PollCardProps>(
  ({ poll, onVote, onReaction, onBookmark, onDelete, ...props }, ref) => {
    const { data: userSession } = authClient.useSession();
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const totalVotes = poll.options.reduce((sum, o) => sum + o.voteCount, 0);
    const location = useLocation();
    const isPollDetailPage = location.pathname.includes(
      `/poll/${poll.shortId}/${poll.slug}`
    );

    return (
      <div
        ref={ref}
        {...props}
        className={cn(
          !isPollDetailPage ? "hover:bg-poll-card-hover-wu" : "",
          "relative px-4 py-4 md:px-6",
          props.disabled
            ? "pointer-events-none cursor-not-allowed opacity-50"
            : ""
        )}
      >
        {!isPollDetailPage ? (
          <Link
            to="/poll/$shortId/$slug"
            search={{ tab: undefined }}
            params={{ shortId: poll.shortId, slug: poll.slug }}
            className="absolute inset-0 z-10"
          >
            <span className="sr-only">Go to poll {poll.question}</span>
          </Link>
        ) : null}
        <div>
          {/* Header */}
          <div className="mb-2 flex items-center justify-between">
            {poll.authorUsername && (
              <div className="flex items-center space-x-3">
                <Link
                  to="/profile/$username"
                  params={{
                    username: poll.authorUsername,
                  }}
                  className="flex h-10 w-10 items-center z-20 justify-center rounded-full transition-transform duration-200 hover:scale-105"
                >
                  <CustomAvatar
                    width="40px"
                    height="40px"
                    name={poll.authorUsername}
                    avatarString={poll.authorAvatar}
                    image={poll.authorImage}
                  />
                </Link>
                <div className="flex items-center gap-1">
                  <Link
                    to="/profile/$username"
                    params={{
                      username: poll.authorUsername,
                    }}
                    className="font-semibold hover:underline dark:text-white z-20"
                  >
                    {poll.authorUsername}
                  </Link>
                  <Dot className="h-5 w-5 dark:text-white" />

                  <p className="text-secondary-text-wu text-sm">
                    {formatDistanceToNow(poll.createdAt, { addSuffix: true })}
                  </p>
                </div>
              </div>
            )}
            {userSession?.user?.id === poll.authorId ? (
              <>
                <DeleteDialog
                  onConfirm={() => onDelete()}
                  isOpen={deleteConfirmOpen}
                  onChangeOpen={setDeleteConfirmOpen}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      aria-label="More options"
                      variant="ghost"
                      size="icon"
                      className="hover:bg-button-hover-background-wu dark:hover:bg-button-hover-background-wu z-10 -mt-1 rounded-full p-2 text-black transition-colors dark:text-white"
                    >
                      <EllipsisVertical className="size-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                    side={"bottom"}
                    align="end"
                    sideOffset={4}
                  >
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        onClick={() => {
                          setDeleteConfirmOpen(true);
                        }}
                      >
                        <Trash />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : null}
          </div>

          {/* Poll Question */}
          <h2 className="dark:text-headline-wu mb-2 text-lg leading-tight font-semibold md:text-xl">
            {poll.question}
          </h2>
          <p className="text-neutral-text-wu mb-4 text-sm">
            {poll.description}
          </p>

          {/* Poll Options */}
          <div className="mb-4 space-y-2">
            {poll.options.map((option) => {
              const percentage =
                totalVotes > 0
                  ? roundToOneDecimal((option.voteCount / totalVotes) * 100)
                  : "0";

              return (
                <motion.button
                  key={option.id}
                  // whileTap={{ scale: 0.97 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onVote(option.id);
                  }}
                  className="group bg-gray-wu relative w-full cursor-pointer overflow-hidden rounded-lg px-4 py-2 text-left"
                >
                  <span className="ring-ring dark:ring-ring pointer-events-none absolute inset-0 z-20 rounded-lg ring-inset group-hover:ring-3" />
                  {poll.selectedOption && (
                    <motion.span
                      className={`absolute inset-0 ${poll.selectedOption === option.id ? "bg-primary-wu" : "bg-primary-light-wu"}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    />
                  )}
                  <div className="relative z-10 flex items-center justify-between gap-4">
                    <span className="font-medium text-gray-800 dark:text-white">
                      {option.text}
                    </span>
                    {poll.selectedOption && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-semibold text-gray-600 dark:text-gray-200">
                          {percentage}%
                        </span>
                        {/* <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({option.voteCount} votes)
                      </span> */}
                      </div>
                    )}
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Stats */}
          {poll.selectedOption && (
            <div className="bg-gray-wu mb-4 rounded-lg p-4">
              <div className="flex items-center justify-between text-sm dark:text-white">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>{totalVotes} total votes</span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="border-border-wu flex items-center justify-between border-t pt-4">
            <div className="z-10" onClick={(e) => e.stopPropagation()}>
              <Rating
                voteScore={poll.reactionScore}
                upvoted={poll.userReaction === "upvote"}
                downvoted={poll.userReaction === "downvote"}
                onVoteChange={(vote) => onReaction(vote)}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Button
                aria-label="Bookmark poll"
                onClick={(e) => {
                  e.stopPropagation();
                  onBookmark();
                }}
                variant="ghost"
                size="icon"
                className={`z-10 rounded-full p-2 transition-colors ${
                  poll.bookmarkedAt
                    ? "bg-yellow-100 text-yellow-600 hover:bg-yellow-100 hover:text-yellow-600 dark:hover:bg-yellow-100"
                    : "hover:bg-button-hover-background-wu dark:hover:bg-button-hover-background-wu text-black dark:text-white"
                }`}
              >
                <Bookmark
                  className={cn(
                    "size-5",
                    poll.bookmarkedAt ? "fill-current" : ""
                  )}
                />
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default PollCard;
